import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Stack,
  Title,
  Text,
  Button,
  Paper,
  Group,
  Alert,
  Collapse,
  Loader,
  Pagination,
  Select,
  Box,
  Tooltip,
  Skeleton,
  ActionIcon,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconAlertCircle,
  IconCalendar,
  IconArrowUp,
  IconArrowDown,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAppUser } from '../hooks/useAppUser';
import { useMealPlanning } from '../hooks/useMealPlanning';
import { MobileOptimized } from '../components/Layout/MobileOptimized';
import { MealPlan, MealPlanGenerationProgress, Recipe } from '../types/mealPlan';
import { AssignmentThumbnail, assignmentThumbnailLabel, hasAssignmentThumbnail, stripAssignments } from '../components/MealPlanning/AssignmentThumbnail';
import { getStatusBadge, formatCreatedDate, formatRecipeSource, formatPlanMealsSummary, formatDateRange, isRecipeScaled, getExpectedDays } from '../utils/mealPlanUtils';
import { showMealPlanDeletedNotification } from '../utils/mealPlanNotifications';
import { freshestProgress, withFreshestGenerationProgress } from '../utils/progressFreshness';
import { PlanGenerationProgress } from '../components/Dashboard/PlanGenerationProgress';
import { MealPlanCardMenu } from '../components/MealPlan/MealPlanCardMenu';
import { buildRecipePath, buildPlanPath } from '../utils/routeHelpers';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useFirestoreTrigger, FirestoreTriggerSnapshot } from '../hooks/useFirestoreTrigger';
import { getPageCache, setPageCache, subscribePageCache } from '../utils/pageCache';
import { nextMealPlansFetch, isLatestMealPlansFetch } from '../utils/mealPlansFetchGuard';
import { restoreMealPlanInCache } from '../utils/mealPlansCache';
import { formatCount } from '../utils/formatCount';

const PAGE_SIZE = 10;

const VALID_FILTERS = ['all', 'upcoming', 'active', 'draft', 'completed', 'expired', 'creating', 'failed'];
// Plans can't overlap, so start-date order and end-date order are identical —
// a single "date" sort covers both (legacy startDate/endDate params map to it).
const VALID_SORT_FIELDS = ['date', 'created', 'status'];

const getDerivedStatusOrder = (plan: MealPlan): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];
  const gen = plan.generationStatus.status;
  const isPast = plan.endDate < todayStr;

  if (gen === 'pending' || gen === 'generating') return 1; // Creating
  if (gen === 'failed') return 6; // Failed
  if (gen === 'completed') {
    if (!isPast && plan.status === 'active') return 2; // Active
    if (!isPast && plan.status === 'draft') return 3; // Draft
    if (isPast && plan.status === 'active') return 4; // Completed
    if (isPast && plan.status === 'draft') return 5; // Expired
  }
  return 7;
};

export const MealPlanHistoryPage = () => {
  usePageTitle('Meal Plan History | Meals You Love');
  const { user } = useAppUser();
  const { getUserMealPlans, deleteMealPlan, error } = useMealPlanning();
  const navigate = useNavigate();

  // Seed from the plans this session already fetched (here or on the
  // dashboard — both cache the same list) so a back navigation renders the
  // list on the first paint; the mount fetch then refreshes it silently.
  const plansCacheKey = user ? `meal-plans:${user.uid}` : null;
  const seededPlans = getPageCache<MealPlan[]>(plansCacheKey);
  const [allMealPlans, setAllMealPlans] = useState<MealPlan[]>(seededPlans ?? []);
  const [loading, setLoading] = useState(!seededPlans);

  // Filter/sort/page live in the URL so they survive navigating into a plan
  // and back, and so links like /meal-plans?filter=upcoming work.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawFilter = searchParams.get('filter') || 'all';
  const statusFilter = VALID_FILTERS.includes(rawFilter) ? rawFilter : 'all';
  const rawSort = searchParams.get('sort') || 'date';
  const sortField =
    rawSort === 'startDate' || rawSort === 'endDate'
      ? 'date'
      : VALID_SORT_FIELDS.includes(rawSort)
        ? rawSort
        : 'date';
  const sortDirection: 'asc' | 'desc' = searchParams.get('dir') === 'desc' ? 'desc' : 'asc';
  const requestedPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  // Default values stay out of the URL to keep it clean
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Freshest progress seen per in-flight plan, so a REST refetch that
  // resolves after newer listener progress cannot rewind the bar (the
  // backwards-flicker bug). Rebuilt each snapshot, so finished plans age out.
  const liveProgressRef = useRef<
    Map<string, MealPlanGenerationProgress | undefined>
  >(new Map());

  const fetchMealPlans = useCallback(async (showLoading: boolean = true) => {
    if (!user) return;

    if (showLoading) {
      setLoading(true);
    }
    const fetchToken = nextMealPlansFetch();
    try {
      const plans = await getUserMealPlans();
      // A newer list fetch superseded this one — drop the stale response.
      if (!isLatestMealPlansFetch(fetchToken)) return;

      // Store all plans without initial sorting (we'll sort in the filtering
      // effect). This fetch read the plan docs when it started; the listener
      // may have delivered newer progress since, and the freshest copy wins.
      const freshPlans = withFreshestGenerationProgress(plans, liveProgressRef.current);
      setAllMealPlans(freshPlans);
      setPageCache(`meal-plans:${user.uid}`, freshPlans);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (user && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      // With seeded content this is a silent background refresh.
      fetchMealPlans(!getPageCache(`meal-plans:${user.uid}`));
    }
  }, [user, fetchMealPlans]);

  // Track plan-list writes made by other surfaces while this page is mounted
  // (e.g. a delete's Undo whose refetch ran in another page's closure) —
  // whoever fetches writes the shared cache; this keeps the list in sync.
  useEffect(() => {
    return subscribePageCache<MealPlan[]>(plansCacheKey, (plans) => {
      setAllMealPlans(withFreshestGenerationProgress(plans, liveProgressRef.current));
      setLoading(false);
    });
  }, [plansCacheKey]);

  // Filter and sort meal plans
  const filteredMealPlans = useMemo(() => {
    let filtered = allMealPlans;
    
    if (statusFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      
      if (statusFilter === 'active') {
        // Active: generation completed, current/future, and activated
        filtered = allMealPlans.filter(plan => 
          plan.generationStatus.status === 'completed' && 
          plan.endDate >= todayStr && 
          plan.status === 'active'
        );
      } else if (statusFilter === 'draft') {
        // Draft: generation completed, current/future, but not activated
        filtered = allMealPlans.filter(plan => 
          plan.generationStatus.status === 'completed' && 
          plan.endDate >= todayStr && 
          plan.status === 'draft'
        );
      } else if (statusFilter === 'completed') {
        // Completed: generation completed, past end date, AND was active
        filtered = allMealPlans.filter(plan => 
          plan.generationStatus.status === 'completed' && 
          plan.endDate < todayStr && 
          plan.status === 'active'
        );
      } else if (statusFilter === 'expired') {
        // Expired: generation completed, past end date, but was never activated
        filtered = allMealPlans.filter(plan => 
          plan.generationStatus.status === 'completed' && 
          plan.endDate < todayStr && 
          plan.status === 'draft'
        );
      } else if (statusFilter === 'creating') {
        // Creating: pending or generating
        filtered = allMealPlans.filter(plan => 
          plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating'
        );
      } else if (statusFilter === 'upcoming') {
        // Upcoming: any non-failed plan with start date today or in the future
        filtered = allMealPlans.filter(plan => {
          // Only consider non-failed plans
          if (plan.generationStatus.status === 'failed') {
            return false;
          }
          
          // Check if start date is today or in the future
          return plan.startDate >= todayStr;
        });
      } else {
        // Other statuses (failed)
        filtered = allMealPlans.filter(plan => plan.generationStatus.status === statusFilter);
      }
    }
    
    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'created':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'status':
          aValue = getDerivedStatusOrder(a);
          bValue = getDerivedStatusOrder(b);
          break;
        case 'date':
        default:
          // Plans can't overlap, so start date order == end date order
          aValue = a.startDate;
          bValue = b.startDate;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allMealPlans, statusFilter, sortField, sortDirection]);

  // Realtime refresh while there are active generations: watch the user's
  // in-flight meal plan docs and refetch the list whenever one changes or
  // reaches a terminal status (i.e. leaves the query).
  const hasActiveGenerations = allMealPlans.some(plan =>
    plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating'
  );
  const inFlightPlansQuery = useMemo(
    () =>
      user?.uid
        ? query(
            collection(db, 'users', user.uid, 'meal_plans'),
            where('generationStatus.status', 'in', ['pending', 'generating'])
          )
        : null,
    [user?.uid]
  );
  // Statuses as of the last snapshot, so progress ticks can be told apart from
  // a plan actually entering, leaving, or changing status.
  const inFlightStatusesRef = useRef<Map<string, string>>(new Map());

  const handleInFlightChange = useCallback(
    (snapshot: FirestoreTriggerSnapshot) => {
      // Polling fallback ticks carry no snapshot, so they refetch as before.
      if (!snapshot || !('docs' in snapshot)) {
        fetchMealPlans(false);
        return;
      }

      const statuses = new Map<string, string>();
      const progressById = new Map<
        string,
        MealPlanGenerationProgress | undefined
      >();
      snapshot.docs.forEach((doc) => {
        const data = doc.data() as Partial<MealPlan>;
        statuses.set(doc.id, data.generationStatus?.status ?? '');
        // Guarded against the snapshot itself being stale: a fresh subscribe
        // can replay a cached snapshot older than what is already on screen.
        progressById.set(
          doc.id,
          freshestProgress(
            liveProgressRef.current.get(doc.id),
            data.generationProgress
          )
        );
      });
      liveProgressRef.current = progressById;

      const previous = inFlightStatusesRef.current;
      // An empty snapshot always refetches: the work may have finished between
      // the last fetch and this subscription, and nothing else would notice.
      const structuralChange =
        statuses.size === 0 ||
        statuses.size !== previous.size ||
        Array.from(statuses).some(([id, status]) => previous.get(id) !== status);
      inFlightStatusesRef.current = statuses;

      // Progress arrives every few seconds. Taking it straight off the snapshot
      // keeps the row moving without a full plan refetch per meal.
      setAllMealPlans((plans) =>
        plans.map((plan) =>
          progressById.has(plan.id)
            ? { ...plan, generationProgress: progressById.get(plan.id) }
            : plan
        )
      );

      if (structuralChange) {
        fetchMealPlans(false); // Refresh meal plans without loading spinner
      }
    },
    [fetchMealPlans]
  );

  useFirestoreTrigger({
    enabled: hasActiveGenerations,
    target: inFlightPlansQuery,
    onTrigger: handleInFlightChange,
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewPlan = (plan: MealPlan) => {
    if (!user) return;
    navigate(buildPlanPath(plan, user.uid));
  };

  const handleRecipeClick = (recipe: Recipe, mealPlanId?: string, assignmentId?: string) => {
    if (!user) return;
    navigate(buildRecipePath(recipe, user.uid), {
      state: {
        // Only pass recipe state if it differs from canonical recipe
        recipe: isRecipeScaled(recipe) ? recipe : undefined,
        ...(mealPlanId && assignmentId && { mealPlanId, assignmentId })
      }
    });
  };

  const handleDeleteMealPlan = useCallback(async (mealPlan: MealPlan) => {
    const success = await deleteMealPlan(mealPlan.id);
    if (success) {
      showMealPlanDeletedNotification({
        mealPlan,
        getIdToken: async () => user?.getIdToken(),
        onRestored: () => {
          // Put the plan straight back (we hold the full object) so
          // reappearing doesn't depend on the refetch — the cache write
          // notifies this page's subscription synchronously.
          if (user?.uid) {
            restoreMealPlanInCache(user.uid, mealPlan);
          }
          return fetchMealPlans(false);
        },
      });
      // Refresh the list in place: the card's own control is already showing
      // the delete in progress, so throwing the page back to its loading
      // state would only make the plan disappear twice.
      await fetchMealPlans(false);
    } else {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete meal plan. Please try again.',
        color: 'red',
      });
    }
  }, [deleteMealPlan, fetchMealPlans, user]);

  // A failed plan's Clear button is a delete, so it takes a couple of seconds
  // to land; the button spins for its own plan until the card is gone.
  const [clearingPlanId, setClearingPlanId] = useState<string | null>(null);

  const handleClearFailedPlan = useCallback(async (plan: MealPlan) => {
    setClearingPlanId(plan.id);
    try {
      await handleDeleteMealPlan(plan);
    } finally {
      setClearingPlanId(null);
    }
  }, [handleDeleteMealPlan]);

  // Paginate filtered meal plans, clamping in case the URL page is out of range
  const totalPages = Math.ceil(filteredMealPlans.length / PAGE_SIZE);
  const currentPage = Math.min(requestedPage, Math.max(totalPages, 1));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedMealPlans = filteredMealPlans.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <MobileOptimized>
      <Stack gap="lg">
        {/* Header */}
        <Group>
          <ActionIcon
            variant="subtle"
            onClick={handleBack}
            size="lg"
          >
            <IconChevronLeft size={20} />
          </ActionIcon>
          <Stack gap={0} className="page-header">
            <Title order={2}>Meal plan history</Title>
            <Text c="dimmed" size="sm" className="page-subtitle">
              View past and current meal plans
            </Text>
          </Stack>
        </Group>

        <Collapse in={!!error}>
          <Alert icon={<IconAlertCircle size={16} color="red" />} color="red" radius="lg">
            <Text size="sm" c="red.7">{error}</Text>
          </Alert>
        </Collapse>

        {loading ? (
          <>
            {/* Skeleton filter bar */}
            <Paper p="md" radius="lg" withBorder>
              <Group gap="md" align="center">
                <Skeleton height={20} width={40} />
                <Skeleton height={36} width={200} radius="md" />
              </Group>
            </Paper>
            {/* Skeleton count/sort row */}
            <Group justify="space-between" align="center">
              <Skeleton height={20} width={140} />
              <Group gap="xs">
                <Skeleton height={36} width={140} radius="md" />
                <Skeleton height={28} width={28} radius="md" />
              </Group>
            </Group>
            {/* Skeleton meal plan cards */}
            <Stack gap="sm">
              {Array.from({ length: 4 }).map((_, i) => (
                <Paper key={i} p="md" radius="lg" withBorder>
                  {/* Mobile skeleton */}
                  <Box hiddenFrom="sm">
                    <Stack gap="sm">
                      <Group gap="sm">
                        <Skeleton height={20} width="50%" />
                        <Skeleton height={20} width={60} radius="xl" />
                      </Group>
                      <Skeleton height={14} width="80%" />
                      <Group gap="xs">
                        <Skeleton w={80} h={80} radius="md" />
                        <Skeleton w={80} h={80} radius="md" />
                        <Skeleton w={80} h={80} radius="md" />
                      </Group>
                      <Skeleton height={36} width="100%" radius="md" />
                    </Stack>
                  </Box>
                  {/* Desktop skeleton */}
                  <Box visibleFrom="sm">
                    <Group align="flex-start" gap="lg">
                      <Group gap="xs">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <Skeleton key={j} w={80} h={80} radius="sm" />
                        ))}
                      </Group>
                      <Stack gap="xs" style={{ flex: 1 }}>
                        <Group justify="space-between">
                          <Stack gap={4}>
                            <Group gap="sm">
                              <Skeleton height={20} width={180} />
                              <Skeleton height={20} width={60} radius="xl" />
                            </Group>
                            <Skeleton height={14} width={250} />
                          </Stack>
                          <Skeleton height={36} width={60} radius="md" />
                        </Group>
                      </Stack>
                    </Group>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </>
        ) : allMealPlans.length === 0 ? (
          <Paper p="xl" radius="lg" ta="center">
            <IconCalendar size={48} color="var(--mantine-color-gray-4)" />
            <Text c="dimmed" mt="md">
              No meal plans found. Create your first meal plan to get started!
            </Text>
            <Button mt="md" onClick={() => navigate('/planning')}>
              Create Meal Plan
            </Button>
          </Paper>
        ) : (
          <>
            {/* Filter Controls */}
            <Paper p="md" radius="lg" withBorder>
              <Group gap="md" align="center">
                <Text fw={500} size="sm" c="dark">Status</Text>
                <Select
                  placeholder="All statuses"
                value={statusFilter}
                onChange={(value) => {
                  const newFilter = value || 'all';
                  updateParams({
                    filter: newFilter === 'all' ? null : newFilter,
                    page: null,
                  });
                }}
                data={[
                  { value: 'all', label: 'All' },
                  { value: 'upcoming', label: 'Upcoming' },
                  { value: 'active', label: 'Active' },
                  { value: 'draft', label: 'Draft' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'creating', label: 'Creating' },
                  { value: 'failed', label: 'Failed' },
                ]}
                size="sm"
                clearable
                style={{ minWidth: 200, maxWidth: 250 }}
                />
              </Group>
            </Paper>

            {/* Count and Sort Options */}
            <Group justify="space-between" align="center">
              <Text fw={500}>
                {filteredMealPlans.length} plan{filteredMealPlans.length !== 1 ? 's' : ''} found
                {filteredMealPlans.length > PAGE_SIZE && (
                  <Text component="span" c="dimmed" size="sm">
                    {' '}• Showing {formatCount(startIndex + 1)}-{formatCount(Math.min(startIndex + PAGE_SIZE, filteredMealPlans.length))} of {formatCount(filteredMealPlans.length)}
                  </Text>
                )}
              </Text>
              <Group gap="xs">
                <Select
                  placeholder="Sort by"
                  value={sortField}
                  onChange={(value) => {
                    const newSortField = value || 'date';
                    updateParams({
                      sort: newSortField === 'date' ? null : newSortField,
                      page: null,
                    });
                  }}
                  data={[
                    { value: 'date', label: 'Plan date' },
                    { value: 'created', label: 'Date created' },
                    { value: 'status', label: 'Status' },
                  ]}
                  size="sm"
                  w={140}
                />
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => {
                    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
                    updateParams({
                      dir: newDirection === 'asc' ? null : newDirection,
                      page: null,
                    });
                  }}
                  title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortDirection === 'asc' ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
                </ActionIcon>
              </Group>
            </Group>

            {/* Meal Plans List */}
            <Stack gap="sm">
              {paginatedMealPlans.map((plan) => (
                <Paper key={plan.id} p="md" radius="lg" withBorder>
                  {/* Mobile layout - vertical stack */}
                  <Box hiddenFrom="sm">
                    <Stack gap="sm">
                      <Group gap="sm" align="center" justify="space-between" wrap="nowrap">
                        <Group gap="sm" align="center" style={{ minWidth: 0 }}>
                          <Text fw={500}>
                            {formatDateRange(plan)}
                          </Text>
                          {getStatusBadge(plan)}
                        </Group>
                        <MealPlanCardMenu
                          mealPlan={plan}
                          userId={user?.uid || ''}
                          onDeletePlan={handleDeleteMealPlan}
                          onPlanUpdated={() => fetchMealPlans(false)}
                          profilePublic={user?.profilePublic}
                          calendarConnected={user?.familyProfile?.googleCalendarConnected ?? false}
                        />
                      </Group>

                      <Text size="sm" c="dimmed">
                        Created {formatCreatedDate(plan.createdAt)} • {formatRecipeSource(plan.preferences.recipeSources)}
                        {plan.generationStatus.status === 'completed' && plan.assignments && (
                          <> • {formatPlanMealsSummary(plan)}</>
                        )}
                      </Text>
                      
                      {plan.generationStatus.status === 'completed' && stripAssignments(plan).length > 0 && (
                        <Group gap="xs">
                          {stripAssignments(plan).slice(0, 3).map((assignment, index) => {
                            if (!hasAssignmentThumbnail(assignment, plan)) return null;
                            return (
                            <Box key={index} pos="relative">
                              <AssignmentThumbnail
                                assignment={assignment}
                                plan={plan}
                                radius="md"
                                onRecipeClick={(recipe) => handleRecipeClick(recipe, plan.id, assignment.id)}
                              />
                              {index === 2 && stripAssignments(plan).length > 3 && (
                                <Tooltip
                                  label={stripAssignments(plan).slice(2).map(a => assignmentThumbnailLabel(a, plan)).filter(Boolean).join(', ')}
                                  position="top" 
                                  withArrow
                                  multiline
                                  maw={300}
                                  events={{ hover: true, focus: true, touch: true }}
                                >
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    bg="rgba(0, 0, 0, 0.6)"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: 'var(--mantine-radius-md)',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Text size="xs" c="white" fw={500}>
                                      +{stripAssignments(plan).length - 2}
                                    </Text>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                            );
                          })}
                        </Group>
                      )}
                      
                      {/* Stands where the three thumbnails above will land, so
                          the card keeps its shape and the week fills in. */}
                      {(plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating') && (
                        <PlanGenerationProgress
                          variant="tiles"
                          maxTiles={3}
                          tileRadius="md"
                          progress={plan.generationProgress}
                          startedAt={plan.createdAt}
                          household={plan.profileSnapshot}
                          expectedDays={getExpectedDays(plan).map((d) => d.dayName)}
                          penciledMeals={plan.preferences.penciledMeals}
                          additionalMeals={plan.preferences.additionalMeals}
                        />
                      )}

                      {plan.generationStatus.status === 'failed' && plan.generationStatus.errorMessage && (
                        <Text size="sm" c="red">
                          {plan.generationStatus.errorMessage}
                        </Text>
                      )}

                      {plan.generationStatus.status === 'completed' ? (
                        <Button
                          size="sm"
                          variant="light"
                          onClick={() => handleViewPlan(plan)}
                          fullWidth
                        >
                          View
                        </Button>
                      ) : plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating' ? (
                        <PlanGenerationProgress
                          variant="compact"
                          progress={plan.generationProgress}
                          startedAt={plan.createdAt}
                          household={plan.profileSnapshot}
                          expectedDays={getExpectedDays(plan).map((d) => d.dayName)}
                          penciledMeals={plan.preferences.penciledMeals}
                          additionalMeals={plan.preferences.additionalMeals}
                        />
                      ) : plan.generationStatus.status === 'failed' ? (
                        <Button
                          size="sm"
                          variant="light"
                          color="red"
                          onClick={() => handleClearFailedPlan(plan)}
                          loading={clearingPlanId === plan.id}
                          fullWidth
                        >
                          Clear
                        </Button>
                      ) : null}
                    </Stack>
                  </Box>

                  {/* Desktop layout - horizontal with thumbnails on left */}
                  <Box visibleFrom="sm">
                    <Group align="flex-start" gap="lg">
                      {/* Left: Recipe thumbnails */}
                      {plan.generationStatus.status === 'completed' || plan.generationStatus.status === 'failed' ? (
                        <Group gap="xs">
                          {stripAssignments(plan).slice(0, 7).map((assignment, index) => {
                            if (!hasAssignmentThumbnail(assignment, plan)) return null;
                            return (
                            <Box key={index} pos="relative">
                              <AssignmentThumbnail
                                assignment={assignment}
                                plan={plan}
                                radius="sm"
                                onRecipeClick={(recipe) => handleRecipeClick(recipe, plan.id, assignment.id)}
                              />
                              {index === 6 && stripAssignments(plan).length > 7 && (
                                <Tooltip
                                  label={stripAssignments(plan).slice(6).map(a => assignmentThumbnailLabel(a, plan)).filter(Boolean).join(', ')}
                                  position="top" 
                                  withArrow
                                  multiline
                                  maw={300}
                                  events={{ hover: true, focus: true, touch: true }}
                                >
                                  <Box
                                    pos="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    bg="rgba(0, 0, 0, 0.6)"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: 'var(--mantine-radius-sm)',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Text size="xs" c="white" fw={500}>
                                      +{stripAssignments(plan).length - 6}
                                    </Text>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                            );
                          })}
                        </Group>
                      ) : (
                        <PlanGenerationProgress
                          variant="tiles"
                          progress={plan.generationProgress}
                          startedAt={plan.createdAt}
                          household={plan.profileSnapshot}
                          expectedDays={getExpectedDays(plan).map((d) => d.dayName)}
                          penciledMeals={plan.preferences.penciledMeals}
                          additionalMeals={plan.preferences.additionalMeals}
                        />
                      )}

                      {/* Right: Meal plan details */}
                      <Stack gap="xs" style={{ flex: 1 }}>
                        {/* nowrap + shrinkable left column: a long meta line
                            must wrap within its own column, never push the
                            View/menu cluster off the top-right corner */}
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                            <Group gap="sm" align="center">
                              <Text fw={500} size="lg">
                                {formatDateRange(plan)}
                              </Text>
                              {getStatusBadge(plan)}
                            </Group>
                            
                            <Text size="sm" c="dimmed">
                              Created {formatCreatedDate(plan.createdAt)} • {formatRecipeSource(plan.preferences.recipeSources)}
                              {plan.generationStatus.status === 'completed' && plan.assignments && (
                                <> • {formatPlanMealsSummary(plan)}</>
                              )}
                            </Text>
                            
                            {plan.generationStatus.status === 'failed' && plan.generationStatus.errorMessage && (
                              <Text size="sm" c="red">
                                {plan.generationStatus.errorMessage}
                              </Text>
                            )}
                          </Stack>

                          {/* Desktop action button */}
                          <Group gap="xs" wrap="nowrap" align="center">
                            {plan.generationStatus.status === 'completed' ? (
                              <Button
                                size="sm"
                                variant="light"
                                onClick={() => handleViewPlan(plan)}
                              >
                                View
                              </Button>
                            ) : plan.generationStatus.status === 'pending' || plan.generationStatus.status === 'generating' ? (
                              <Group justify="center" w={60}>
                                <Loader size="sm" />
                              </Group>
                            ) : plan.generationStatus.status === 'failed' ? (
                              <Button
                                size="sm"
                                variant="light"
                                color="red"
                                onClick={() => handleClearFailedPlan(plan)}
                                loading={clearingPlanId === plan.id}
                              >
                                Clear
                              </Button>
                            ) : null}
                            <MealPlanCardMenu
                              mealPlan={plan}
                              userId={user?.uid || ''}
                              onDeletePlan={handleDeleteMealPlan}
                              onPlanUpdated={() => fetchMealPlans(false)}
                              profilePublic={user?.profilePublic}
                              calendarConnected={user?.familyProfile?.googleCalendarConnected ?? false}
                            />
                          </Group>
                        </Group>
                      </Stack>
                    </Group>
                  </Box>
                </Paper>
              ))}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Group justify="center" mt="lg">
                <Pagination
                  value={currentPage}
                  onChange={(page) => updateParams({ page: page === 1 ? null : String(page) })}
                  total={totalPages}
                  size="sm"
                  siblings={0}
                />
              </Group>
            )}
          </>
        )}
      </Stack>
    </MobileOptimized>
  );
};
